import React, { useEffect, useState } from 'react';
import { getOrgChart, OrgChartNode } from '../dataProvider';

/**
 * Recursively renders a single org chart node and its children.
 */
const OrgChartNodeItem: React.FC<{ node: OrgChartNode }> = ({ node }) => (
    <li data-testid={`org-node-${node.employee_id}`}>
        <span className="org-node-label">
            <strong>{node.full_name}</strong>
            {' '}
            <span className="org-node-role">({node.role})</span>
        </span>
        {node.children.length > 0 && (
            <ul>
                {node.children.map(child => (
                    <OrgChartNodeItem key={child.employee_id} node={child} />
                ))}
            </ul>
        )}
    </li>
);

/**
 * OrgChart component
 *
 * Renders the internal user organization chart as a hierarchical tree.
 * Fetches data from `GET /users/org-chart` via the dataProvider helper.
 */
export const OrgChart: React.FC = () => {
    const [roots, setRoots] = useState<OrgChartNode[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        getOrgChart()
            .then(data => {
                setRoots(data);
                setLoading(false);
            })
            .catch((err: Error) => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    if (loading) {
        return <div data-testid="org-chart-loading">Loading organization chart…</div>;
    }

    if (error) {
        return <div data-testid="org-chart-error">Error loading org chart: {error}</div>;
    }

    if (roots.length === 0) {
        return <div data-testid="org-chart-empty">No employees found.</div>;
    }

    return (
        <div data-testid="org-chart">
            <h2>Organization Chart</h2>
            <ul>
                {roots.map(root => (
                    <OrgChartNodeItem key={root.employee_id} node={root} />
                ))}
            </ul>
        </div>
    );
};
